import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { LearnerInlineContentLoader } from '../shared/LearnerInlineContentLoader';
import { CollapsibleFadePanel } from '../shared/CollapsibleFadePanel';
import { subscriptionService } from '../../services/subscriptionService';
import type { SubscriptionActivityItem } from '../../types/subscription';
import { SubscriptionPlanIconGlyph, subscriptionPlanBadgeStyle } from '../../utils/subscriptionPlanIcon';
import { useAppLocale } from '../../providers/LocaleProvider';
import {
  formatSubscriptionActivityOccurredAt,
  getSubscriptionActivityDetail,
  getSubscriptionActivityTitle,
} from '../../utils/subscriptionActivityLabels';

export const ProfileSubscriptionHistory: React.FC = () => {
  const { t } = useTranslation('subscription');
  const { t: tCommon } = useTranslation('common');
  const { locale } = useAppLocale();
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<SubscriptionActivityItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const loadedRef = React.useRef(false);

  const activityLabel = (type: string) => {
    const map: Record<string, string> = {
      xp_redeem: t('activityXpRedeem'),
      purchase: t('activityPurchase'),
      admin_assign: t('activityAdminAssign'),
      subscription: t('activitySubscription'),
    };
    return map[type] ?? type;
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const activity = await subscriptionService.getSubscriptionActivity(100);
      setItems(activity);
      loadedRef.current = true;
    } catch (e) {
      setError(e instanceof Error ? e.message : t('historyLoadFailed'));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (expanded && !loadedRef.current) {
      void load();
    }
  }, [expanded, load]);

  return (
    <div className="clay-card bg-white dark:bg-gray-800 dark:border dark:border-gray-700 overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-gray-50/80 dark:hover:bg-gray-700/40 transition-colors"
        aria-expanded={expanded}
      >
        <div>
          <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200">{t('historyTitle')}</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{t('historySubtitle')}</p>
        </div>
        <ChevronDown
          className={`w-5 h-5 text-gray-500 shrink-0 transition-transform duration-300 ease-in-out ${
            expanded ? 'rotate-180' : ''
          }`}
          aria-hidden
        />
      </button>
      <CollapsibleFadePanel expanded={expanded} className="border-t border-gray-200 dark:border-gray-700">
          <div className="px-5 py-4">
            {loading ? (
              <LearnerInlineContentLoader message={t('loadingHistory')} />
            ) : error ? (
              <div className="text-center py-6">
                <p className="text-sm text-red-600 dark:text-red-400 mb-3">{error}</p>
                <button
                  type="button"
                  onClick={() => void load()}
                  className="text-sm font-semibold text-blue-600 hover:text-blue-700"
                >
                  {tCommon('errors.tryAgain')}
                </button>
              </div>
            ) : items.length === 0 ? (
              <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-6">{t('noActivity')}</p>
            ) : (
              <ul className="space-y-3">
                {items.map((item) => {
                  const title = getSubscriptionActivityTitle(item, t);
                  const detail = getSubscriptionActivityDetail(item, t, locale);
                  return (
                    <li
                      key={item.id}
                      className="flex gap-3 rounded-xl border border-gray-200 dark:border-gray-700 p-3 bg-gray-50/80 dark:bg-gray-900/40"
                    >
                      <span
                        className="shrink-0 inline-flex items-center justify-center h-10 w-10 rounded-lg border border-gray-200 dark:border-gray-600"
                        style={subscriptionPlanBadgeStyle(item.planColor)}
                      >
                        <SubscriptionPlanIconGlyph
                          icon={item.planIcon ?? null}
                          color={item.planColor ?? null}
                          className="h-5 w-5"
                        />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold text-gray-900 dark:text-gray-100 text-sm">{title}</span>
                          <span className="text-[10px] uppercase tracking-wide font-medium px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                            {activityLabel(item.activityType)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 dark:text-gray-300 mt-0.5">{item.planName}</p>
                        {detail ? (
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{detail}</p>
                        ) : null}
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                          {item.occurredAt ? formatSubscriptionActivityOccurredAt(item.occurredAt, locale) : '—'}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
      </CollapsibleFadePanel>
    </div>
  );
};

export default ProfileSubscriptionHistory;
